import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../../extra/Table";
import Iconb from "../../extra/Iconb";
import { connect, useDispatch, useSelector } from "react-redux";
import { getConversations, blockConversation } from "../../store/chat/chat.action";
import ToggleSwitch from "../../extra/ToggleSwitch";
import dayjs from "dayjs";
import Pagination from "../../extra/Pagination";
import Searching from "../../extra/Searching";
import Skeleton from "react-loading-skeleton";
import { colors } from "../../../util/SkeletonColor";
import "react-loading-skeleton/dist/skeleton.css";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BlockIcon from "@mui/icons-material/Block";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

const Chat = (props) => {
  const [size, setSize] = useState(10);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { conversations, totalConversations } = useSelector((state) => state.chat);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getConversations(currentPage, size));
  }, [dispatch, currentPage, size]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setData(conversations);
  }, [conversations]);

  // pagination
  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event), 10);
    setSize(parseInt(event), 10);
    setCurrentPage(1);
  };

  const handleOpen = (id) => {
    navigate("/admin/chatDetails", {
      state: id,
    });
  };

  const handleBlock = (conversation) => {
    props.blockConversation(conversation._id);
  };

  // searching
  const handleFilterData = (filteredData) => {
    if (typeof filteredData === "string") {
      setSearch(filteredData);
    } else {
      setData(filteredData);
    }
  };

  const mapData = [
    {
      Header: "No",
      width: "20px",
      Cell: ({ index }) => (
        <span className="text-white">
          {(currentPage - 1) * rowsPerPage + index + 1}
        </span>
      ),
    },
    {
      Header: "User",
      body: "user",
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <div className="position-relative">
            {loading ? (
              <Skeleton
                height={42}
                width={42}
                style={{ borderRadius: "90px" }}
                baseColor={colors?.baseColor}
                highlightColor={colors?.highlightColor}
              />
            ) : (
              <img
                src={row?.user?.image}
                height={45}
                width={45}
                alt=""
                style={{ borderRadius: "90px", objectFit: "cover" }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/dummy.png";
                }}
              />
            )}
          </div>
          <span className="fw-normal ms-3 text-start">
            <b className="fw-normal text-capitalize text-white">
              {row.user?.firstName} {row.user?.lastName}
            </b>
          </span>
        </div>
      ),
      sorting: false,
    },
    {
      Header: "Seller",
      body: "seller",
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <div className="position-relative">
            {loading ? (
              <Skeleton
                height={42}
                width={42}
                style={{ borderRadius: "90px" }}
                baseColor={colors?.baseColor}
                highlightColor={colors?.highlightColor}
              />
            ) : (
              <img
                src={row?.seller?.image}
                height={45}
                width={45}
                alt=""
                style={{ borderRadius: "90px", objectFit: "cover" }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/dummy.png";
                }}
              />
            )}
          </div>
          <span className="fw-normal ms-3 text-start">
            <b className="fw-normal text-capitalize text-white">
              {row.seller?.businessName || `${row.seller?.firstName} ${row.seller?.lastName}`}
            </b>
          </span>
        </div>
      ),
      sorting: false,
    },
    {
      Header: "Last Message",
      body: "lastMessage",
      Cell: ({ row }) => (
        <div className="text-white" style={{ maxWidth: "200px" }}>
          <span className="text-truncate d-block">
            {row.lastMessage?.content || "No messages yet"}
          </span>
          <small className="text-secondary">
            {row.lastMessage?.senderType === "User" ? "User" : "Seller"}
          </small>
        </div>
      ),
      sorting: false,
    },
    {
      Header: "Unread",
      body: "unread",
      Cell: ({ row }) => {
        const totalUnread = (row.userUnreadCount || 0) + (row.sellerUnreadCount || 0);
        return (
          <span
            className={`badge ${totalUnread > 0 ? "bg-danger" : "bg-secondary"}`}
            style={{ padding: "8px 12px", fontSize: "12px" }}
          >
            {totalUnread}
          </span>
        );
      },
    },
    {
      Header: "Status",
      body: "status",
      Cell: ({ row }) => {
        const isBlocked = row.isBlockedByUser || row.isBlockedBySeller || !row.isActive;
        return (
          <span
            className={`badge ${isBlocked ? "bg-danger" : "bg-success"}`}
            style={{ padding: "8px 12px", fontSize: "12px" }}
          >
            {isBlocked ? "Blocked" : "Active"}
          </span>
        );
      },
    },
    {
      Header: "Last Activity",
      body: "lastMessageAt",
      Cell: ({ row }) => (
        <span className="text-white">
          {row.lastMessageAt ? dayjs(row.lastMessageAt).format("DD MMM YYYY HH:mm") : "-"}
        </span>
      ),
    },
    {
      Header: "Block",
      body: "isActive",
      Cell: ({ row }) => (
        <div className="">
          <ToggleSwitch
            value={!row.isActive}
            onClick={() => handleBlock(row)}
          />
        </div>
      ),
    },
    {
      Header: "View",
      body: "",
      Cell: ({ row }) => (
        <Iconb
          type="button"
          newClass="boxCenter infobtn userBtn"
          btnIcon={<ChatBubbleOutlineIcon sx={{ color: "#737272" }} />}
          style={{
            borderRadius: "80px",
            margin: "auto",
            height: "38px",
            width: "38px",
          }}
          onClick={() => handleOpen(row._id)}
        />
      ),
    },
  ];

  return (
    <>
      <div className="mainUserTable">
        <div className="userTable">
          <div className="col-12 headname">Chat Management</div>

          <div className="userMain">
            <div className="tableMain mt-2">
              <div className="userHeader primeHeader">
                <div className="row">
                  <div className="col-2"></div>
                  <div className="col-10">
                    <Searching
                      type="client"
                      data={conversations}
                      setData={setData}
                      column={data}
                      onFilterData={handleFilterData}
                      serverSearching={handleFilterData}
                      button={true}
                      setSearchValue={setSearch}
                      searchValue={search}
                    />
                  </div>
                </div>
              </div>
              <Table
                data={data}
                mapData={mapData}
                serverPerPage={rowsPerPage}
                serverPage={currentPage}
                type="server"
              />
              <Pagination
                component="div"
                count={conversations?.length}
                type="server"
                onPageChange={handleChangePage}
                serverPerPage={rowsPerPage}
                totalData={totalConversations}
                serverPage={currentPage}
                setCurrentPage={setCurrentPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          </div>
          <div className="userFooter primeFooter"></div>
        </div>
      </div>
    </>
  );
};

export default connect(null, { getConversations, blockConversation })(Chat);
